import cx from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { ReactNode, useEffect } from "react";

import { AnimatedBackground } from "components/AnimatedBackground";

interface ModalProps {
  /** Header */
  header?: ReactNode;
  /** Body */
  children: ReactNode;
  /** Footer */
  footer?: ReactNode;
  /** Open/hide */
  open?: boolean;
  /** On close callback */
  onClose?: () => void;
  /** Specify the size */
  size?: string;
  /** Whether the modal is full screen */
  fullScreen?: boolean;
  /** Modal class name */
  modalClassName?: string;
  /** Header class name */
  headerClassName?: string;
  /** Body class name */
  bodyClassName?: string;
  /** Footer class name */
  footerClassName?: string;
  /** Animation duration in seconds */
  animationDuration?: number;
  /** Use animated background (only applies with v3 flag) */
  useAnimatedBackground?: boolean;
}

/**
 * Atomic component - Modal
 */
export const Modal = ({
  header,
  children,
  footer,
  open,
  onClose,
  size,
  fullScreen,
  modalClassName = "rounded-t-xl",
  headerClassName,
  bodyClassName,
  footerClassName,
  animationDuration = 0.3,
  useAnimatedBackground = false,
}: ModalProps) => {
  const isV3Enabled = true;
  const shouldUseAnimatedBg = useAnimatedBackground && isV3Enabled;
  // Add overflow-hidden to the body when the modal is open
  // So the modal is the only thing that can be scrolled
  useEffect(() => {
    if (open) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/15"
            onClick={onClose}
            data-testid="div-dismiss-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: animationDuration * 0.67,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className={cx(
              "Modal fixed bottom-0 left-0 right-0 z-50 mx-auto flex max-h-[calc(100%_-_env(safe-area-inset-top)_-_12px)] w-full flex-col sm:w-[390px]",
              !shouldUseAnimatedBg && "bg-white",
              shouldUseAnimatedBg && "overflow-hidden",
              size && size,
              modalClassName
            )}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: animationDuration, ease: "easeInOut" }}
          >
            {shouldUseAnimatedBg ? (
              <AnimatedBackground
                mode="light-prominent"
                className="flex h-full grow flex-col overflow-auto"
              >
                <div className={cx("flex-none", headerClassName)}>{header}</div>
                <div className={cx("grow overflow-auto", bodyClassName)}>
                  {children}
                </div>
                <div
                  className={cx(
                    "flex-none",
                    fullScreen ? "pb-0" : "pb-[env(safe-area-inset-bottom)]",
                    footerClassName
                  )}
                >
                  {footer}
                </div>
              </AnimatedBackground>
            ) : (
              <>
                <div className={cx("flex-none", headerClassName)}>{header}</div>
                <div className={cx("grow overflow-auto", bodyClassName)}>
                  {children}
                </div>
                <div
                  className={cx(
                    "flex-none",
                    fullScreen ? "pb-0" : "pb-[env(safe-area-inset-bottom)]",
                    footerClassName
                  )}
                >
                  {footer}
                </div>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
